<template>
  <OmtLayoutContainer
    bg="#F6F6F9"
    :minHeight="['initial', 'calc(100vh - 80px)']"
  >
    <OmtRow>
      <CFlex
        flexDirection="column"
        width="100%"
        justify="space-between"
        minHeight="calc(100vh - 140px)"
        mt="16px"
      >
        <CBox>
          <OmtTypography
            as="h1-large"
            color="var(--colors-light-primary-dark-blue)"
            mb="30px"
            lineHeight="28px"
          >
            Prepare-se para a captura
          </OmtTypography>

          <OmtTypography
            as="p1"
            color="var(--colors-light-primary-gray1)"
            lineHeight="22px"
            mb="60px"
          >
            A qualidade da avaliação depende de boa luz, rosto visível e estabilidade durante a captura ou leitura.
          </OmtTypography>

          <CBox
            v-for="(card, index) in instructionCards"
            :key="index"
            bg="white"
            border="1px solid #DADADA"
            borderRadius="8px"
            p="16px 20px"
            :mb="index === instructionCards.length - 1 ? '0' : '24px'"
          >
            <CFlex alignItems="center">
              <CFlex
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                mr="12px"
                flexShrink="0"
              >
                <OmtIcons
                  :name="card.icon"
                  :prefix="card.iconPrefix || 'fa-solid'"
                  size="xl"
                  :color="card.iconColor || 'var(--colors-light-primary-dark-blue)'"
                />
              </CFlex>
              <CBox>
                <OmtTypography
                  as="h2-bold"
                  color="var(--colors-light-primary-dark-blue)"
                  lineHeight="20px"
                  mb="4px"
                >
                  {{ card.title }}
                </OmtTypography>
                <OmtTypography
                  as="p0"
                  color="var(--colors-light-primary-gray2)"
                >
                  {{ card.description }}
                </OmtTypography>
              </CBox>
            </CFlex>
          </CBox>

          <CBox
            v-if="hasError"
            bg="#FEF0F0"
            border="1px solid #CD1111"
            borderRadius="8px"
            p="16px 20px"
            mt="30px"
          >
            <OmtTypography
              as="h2-bold"
              color="#9B0B0B"
            >
              <span
                v-if="displayErrorMessage"
                style="white-space: pre-line"
                >{{ displayErrorMessage }}</span
              >
              <span v-else>A imagem aparenta ter problemas,<br />tente novamente</span>
            </OmtTypography>
          </CBox>
        </CBox>

        <CBox
          mt="auto"
          mb="16px"
          width="100%"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            capture="environment"
            style="display: none"
            @change="handleFileChange"
          />
          <OmtButton
            size="lg"
            width="100%"
            borderRadius="95px"
            @click="handleStartCapture"
          >
            Tirar foto
          </OmtButton>
        </CBox>
      </CFlex>
    </OmtRow>
  </OmtLayoutContainer>
</template>

<script>
import { CFlex, CBox } from "@chakra-ui/vue";
import { mapState, mapActions, mapMutations } from "vuex";
import Swal from "sweetalert2";
import { isMobile } from "@/utils/utils";

export default {
  name: "skiniver-instructions",
  components: {
    CFlex,
    CBox
  },
  beforeRouteEnter(to, from, next) {
    if (!isMobile()) {
      next({ name: "skiniver.inicio" });
    } else {
      next();
    }
  },
  data() {
    return {
      instructionCards: [
        {
          icon: "lightbulb",
          iconPrefix: "fa-solid",
          iconColor: "var(--colors-light-support-warning)",
          title: "Boa iluminação",
          description: "Evite sombra forte no rosto."
        },
        {
          icon: "face-viewfinder",
          iconPrefix: "fa-solid",
          title: "Área centralizada",
          description: "Deixe a área visível na câmera."
        },
        {
          icon: "magnifying-glass-waveform",
          iconPrefix: "fa-light",
          title: "Fique parado",
          description: "Evite movimentos durante a captura"
        }
      ]
    };
  },
  computed: {
    ...mapState("skiniver", ["hasImageError", "errorMessage"]),
    hasError() {
      return Boolean(this.hasImageError);
    },
    displayErrorMessage() {
      return this.errorMessage || "A imagem aparenta ter problemas,\ntente novamente";
    }
  },
  mounted() {
    if (!isMobile()) {
      this.$router.replace({ name: "skiniver.inicio" });
      return;
    }
    this.$root.$emit("hide-loading");
  },
  methods: {
    ...mapActions("skiniver", ["uploadFile"]),
    ...mapMutations("skiniver", {
      setImageUrl: "SET_IMAGE_URL",
      setUploadMethod: "SET_UPLOAD_METHOD",
      clearError: "CLEAR_ERROR"
    }),
    handleStartCapture() {
      this.clearError();
      this.setUploadMethod("camera");
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    async handleFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      this.clearError();
      this.setUploadMethod("camera");
      this.$root.$emit("show-loading");
      let resp = {};
      let hasUploadError = false;

      try {
        const filename = file.name || `skiniver_capture_${Date.now()}.jpg`;
        resp = await this.uploadFile({
          filename,
          blobFile: file
        });
      } catch (error) {
        console.error(error);
        hasUploadError = true;
      } finally {
        this.$root.$emit("hide-loading");
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      }

      const uploadedPic = (resp && resp.Data && resp.Data[0]) || (resp && resp.Data);
      const fileRemoteUrl = uploadedPic && (uploadedPic.fileRemoteUrl || uploadedPic.url);

      if (hasUploadError || !fileRemoteUrl || (uploadedPic && uploadedPic.message && uploadedPic.message !== "OK")) {
        await Swal.fire({
          title: "Erro",
          text: (uploadedPic && uploadedPic.message) || "Não foi possível enviar a imagem. Tente novamente.",
          confirmButtonText: "OK",
          icon: "error"
        });
        return;
      }

      this.clearError();
      this.setImageUrl(fileRemoteUrl);
      this.$router.push({
        name: "skiniver.resultado"
      });
    }
  }
};
</script>
